import * as React from 'react';
import { downloadArticlesAudit, getArticleListOverview, getInternalArticles } from '../../api/siteArticlesClientApi';
// import { getArticleReleases } from '../../api/articlesClientApi';
import { Navigate } from 'react-router-dom';
import { ArticleListOverview, InternalSubscriberArticleListing } from '../../amorg-common';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { CardButton } from '../../designSystem/CardButton';
import { colors } from '../../designSystem/colors/color';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { CompletionPanel, SegmentedCompletionPanel } from '../../designSystem/graphs/CompletionPanel';
import { Icon, iconName } from '../../designSystem/icons/Icons';
import { TextInput } from '../../designSystem/inputs/TextInput';
import { Label } from '../../designSystem/Label';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { ToggletipTheOne } from '../../designSystem/ToggletipTheOne';
import { P, Small } from '../../designSystem/typography/Body';
import { H2 } from '../../designSystem/typography/Heading';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { articleIconHelper } from '../../utils/iconHelper';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';

export function ArticlesOverviewPage (): JSX.Element
{
    return (
        <MemberPageWrapper selectedMenuItemKey={'articles'} breadCrumbs={[{ text: 'Lovservice', url: '' }]}>

            <PageHeader 
                headerIcon='balance'
                headerText='Lovservice'
                buttons={
                    <LinkButton variant="outlined" href={downloadArticlesAudit} icon='interface_actions_download' tooltipContent={<div>Download oversigt til Excel</div>} />             } 
            />
            
            <SingleColumnLayout>
                <ArticlesOverviewPageContent />
            </SingleColumnLayout>

        </MemberPageWrapper>
    );
}

function ArticlesOverviewPageContent (): JSX.Element
{
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState<ArticleListOverview | null>(null);
    const [internalArticles, setInternalArticles] = React.useState<InternalSubscriberArticleListing[]>([]);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [redirect, setRedirect] = React.useState('');



    React.useEffect(() =>
    {
        document.title = 'Amorgy - Lovservice';
        const getData = async () =>
        {
            const dataListing = await getArticleListOverview();
            setData(dataListing);

            const internalArticles = await getInternalArticles();
            setInternalArticles(internalArticles);

            setLoading(false);
        };
        getData();
    }, []);

    const internalCompleted = internalArticles?.filter(a => a.evaluationImportance !== null).length || 0;
    const internalMissing = internalArticles?.filter(a => a.evaluationImportance === null).length || 0;

    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewCustomFormAnswerList']);

    return (
        <Flex flexDirection='column' gap={16}>

                {redirect &&
                    <Navigate to={redirect} />
                }

                <LoadingPageOverlay loading={loading} />

                {!hasAccess
                    ? <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>
                    :
                    <>
                    <Flex justifyContent='space-between'>
                        <H2 marginTop={5} marginBottom={5}>Tilgængelige lovkravslister</H2>
                        <TextInput
                            value={searchTerm}
                            onChange={setSearchTerm}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setRedirect(`/articles/list?search=${encodeURIComponent(searchTerm)}`);
                                }
                            }}
                            placeholder='Søg efter lovkrav...'
                            trailingIcon='interface_actions_search'
                        />
                    </Flex>

                    <section>
                        <Flex flexDirection='column' gap={16}>
                            {data !== null && data?.lists.map((list) =>
                                {
                                    const evaluationsMissing = list.topics.reduce((sum, topic) => sum + topic.articlesWithMissingEvaluationsCount, 0);
                                    const evaluationsProvided = list.topics.reduce((sum, topic) => sum + topic.articlesWithNeededEvaluationsProvidedCount, 0);
                                    
                                    const requirementsCompleted = list.topics.reduce((sum, topic) => sum + topic.articlesWithRequirementsCount, 0);
                                    const requirementsMissing = list.topics.reduce((sum, topic) => sum + topic.articlesWithMissingRequirementsCount, 0);

                                    const totalArticles = list.topics.reduce((sum, topic) => sum + topic.articleCount, 0);
                                    const withOwners = list.topics.reduce((sum, topic) => sum + topic.articlesWithOwnersCount, 0);

                                    const lowEvaluation = list.topics.reduce((sum, topic) => sum + topic.articlesEvaluatedLowCount, 0);
                                    const mediumEvaluation = list.topics.reduce((sum, topic) => sum + topic.articlesEvaluatedMediumCount, 0);
                                    const highEvaluation = list.topics.reduce((sum, topic) => sum + topic.articlesEvaluatedHighCount, 0);

                                    return (
                                        <ListCard key={list.listId}
                                            isInternal={false}
                                            iconName={articleIconHelper.getArticleListIcon(list.listName)}
                                            listName={list.listName}
                                            changes={{
                                                neededProvided: evaluationsProvided,
                                                neededMissing: evaluationsMissing
                                            }}
                                            requirements={{
                                                completed: requirementsCompleted,
                                                missing: requirementsMissing
                                            }}
                                            totalArticles={totalArticles}
                                            articlesWithOwners={withOwners}
                                            evaluations={{
                                                low: lowEvaluation,
                                                medium: mediumEvaluation,
                                                high: highEvaluation
                                            }}
                                            actionPlanCount={list.topics.reduce((sum, topic) => sum + topic.openActionPlanCount, 0)}
                                            onClick={() => setRedirect(`/articles/list?lid=${list.listId}`)}
                                        />
                                    );
                            })}

                            <ListCard
                                isInternal
                                iconName='data_files_document'
                                listName='Andre forpligtelser og krav'
                                totalArticles={internalCompleted + internalMissing}
                                articlesWithOwners={internalArticles?.filter(a => a.owner !== null).length || 0}
                                evaluations={{
                                    low: internalArticles?.filter(a => a.evaluationImportance === 'low').length || 0,
                                    medium: internalArticles?.filter(a => a.evaluationImportance === 'medium').length || 0,
                                    high: internalArticles?.filter(a => a.evaluationImportance === 'high').length || 0
                                }}
                                onClick={() => setRedirect('/articles/internal')}
                            />
                        </Flex>
                    </section>
                    </>
                }
                </Flex>
    );
}

type ListCardProps = {
    isInternal: boolean,
    iconName:iconName,
    listName:string,
    evaluations:{
        low:number,
        medium:number,
        high:number
    }
    changes?:{
        neededProvided:number,
        neededMissing:number,
    },
    requirements?:{
        completed:number,
        missing:number
    },
    articlesWithOwners:number,
    totalArticles:number,
    actionPlanCount?:number,
    onClick: () => void,
}
function ListCard(props:ListCardProps):JSX.Element
{
    const evaluationsPct = props.totalArticles === 0
        ? 100
        : Math.round(props.evaluations.low / props.totalArticles * 100);

    let changesPct = props.changes
        ? Math.round((props.changes.neededProvided / (props.changes.neededProvided + props.changes.neededMissing))*100)
        : 0;
    if(props.changes && props.changes.neededMissing === 0)
        changesPct = 100;

    let requirementsPct = props.requirements
        ? Math.round((props.requirements.completed / (props.requirements.completed + props.requirements.missing))*100)
        : 0;
    if(props.requirements && props.requirements.missing === 0)
        requirementsPct = 100;

    const itemText = props.isInternal ? 'krav' : 'lovkrav';

    return (
        <CardButton variant="outlined"
            onClick={props.onClick}>

            <Flex flexDirection="column" gap={32}>

                <Flex gap={64} alignItems='center'>
                    <Icon name={props.iconName} sizeOverride={60} />
                    <div>
                        <H2 >{props.listName}</H2>
                    </div>
                </Flex>

                <Flex>

                    <Flex flexDirection="column" style={{width:'33.33%'}} alignItems="flex-start">
                        
                        <Flex alignItems="center">
                            <Label>Evaluering</Label>
                            <ToggletipTheOne>
                                Af alle {itemText} i listen, er {Math.round((props.evaluations.low/props.totalArticles)*100)}% evalueret til at {itemText}et overholdes
                            </ToggletipTheOne>
                        </Flex>

                        <SegmentedCompletionPanel 
                            completionPct={evaluationsPct}
                            maxWidth="100%"
                            segments={[
                                {
                                    value: props.evaluations.low,
                                    icon: 'status_ok',
                                    color: colors.icon.success,
                                    text: `${props.evaluations.low.toString()} er overholdt`
                                },
                                {
                                    value: props.evaluations.medium,
                                    icon: 'warning',
                                    color: colors.icon.warning,
                                    text: `${props.evaluations.medium.toString()} er delvist overholdt`
                                },
                                {
                                    value: props.evaluations.high,
                                    icon: 'status_error',
                                    color: colors.icon.error,
                                    text: `${props.evaluations.high.toString()} er ikke overholdt`
                                },
                                {
                                    value: props.totalArticles - props.evaluations.low - props.evaluations.medium -  props.evaluations.high,
                                    color: colors.icon.disabled
                                }
                            ]} />

                    </Flex>


                    {props.changes &&
                        <Flex flexDirection="column" style={{width:'33.33%'}} alignItems="flex-start">

                            <Flex alignItems="center">
                                <Label>Ændringer</Label>
                                <ToggletipTheOne>
                                    De {itemText} i listen der har ændringer og som I har evalueret. Dvs. I har læst og forstået ændringerne, og forholdt jer til dem - uagtet overholdelse af lovkravet. Dette er i overensstemmelse med ISO 45001
                                </ToggletipTheOne>
                            </Flex>
                            <CompletionPanel 
                                pct={changesPct}
                                completionText={`${props.changes.neededProvided === 0 ? 'Ingen' : props.changes.neededProvided.toString()} er evalueret`}
                                missingText={`${props.changes.neededMissing === 0 ? 'Ingen' : props.changes.neededMissing.toString()} mangler evaluering`}
                                maxWidth="100%" />

                        </Flex>
                    }
                    
                    {props.requirements 
                         ?  
                         <Flex flexDirection="column" style={{width:'33.33%'}} alignItems="flex-start">

                                <Flex alignItems="center">
                                    <Label>Juridisk praksis</Label>
                                    <ToggletipTheOne>
                                        De {itemText} i listen der har juridiske krav eller praksis, som I har forholdt jer til
                                    </ToggletipTheOne>
                                </Flex>
                                <CompletionPanel 
                                    pct={requirementsPct}  
                                    completionText={`${props.requirements.completed === 0 ? 'Ingen' : props.requirements.completed.toString()} er udfyldt`}
                                    missingText={`${props.requirements.missing === 0 ? 'Ingen' : props.requirements.missing.toString()} mangler`} 
                                    maxWidth="100%" />

                            </Flex>
                        : <div style={{width:'33.33%'}} />
                    }

                    
                </Flex>

                <Flex gap={32}>
                    
                    <NotificationIconText
                        icon='balance'
                        highlighColor={colors.icon.active}
                        templateText={`Listen indeholder $0 ${itemText}`}
                        parameters={[ props.totalArticles===0 ? 'Ingen' : props.totalArticles.toString() ]} />

                    <NotificationIconText
                        icon='interface_user_user'
                        highlighColor={colors.icon.active}
                        templateText={`$0 ${itemText} er tildelt en ansvarlig`}
                        parameters={[ 
                            props.articlesWithOwners === 0 
                                ? 'Ingen' 
                                : props.articlesWithOwners === props.totalArticles
                                    ? 'Alle'
                                    : props.articlesWithOwners.toString() 
                        ]} />

                    {props.actionPlanCount !== undefined &&
                        <NotificationIconText
                            icon='fact_check'
                            highlighColor={colors.icon.active}
                            templateText={`$0 aktive handlingsplaner`}
                            parameters={[
                                props.actionPlanCount === 0 ? 'Ingen' : props.actionPlanCount.toString()
                            ]} />
                    }
                </Flex>
            </Flex>
        </CardButton>
    );
}


function NotificationIconText({icon, highlighColor, templateText, parameters}:
    {icon:iconName, highlighColor: string, templateText:string, parameters: string[]}
):JSX.Element
{
    let finalText = templateText;
    parameters.forEach((param,i) =>
    {
        finalText = finalText.toString().replace(`$${i}`, `<span style="color:${highlighColor}; font-weight:bold;"> ${param} </span>`);
    });

    return (
        <Flex gap={8}>
            <Icon name={icon} color={highlighColor} size="x-small" />
            <Small><div dangerouslySetInnerHTML={{__html: finalText}} /></Small>
        </Flex>
    );
}

